import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Sparkles, Zap, Crown } from 'lucide-react';
import { Button } from '../components/UI';
import { getPlan, createCheckout } from '../api';
import { useAuth } from '../context/AuthContext';

export default function Upgrade({ onBack }) {
  const { user, refresh } = useAuth();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getPlan().then(setPlan).catch(() => {});
  }, []);

  const handleCheckout = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await createCheckout(window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) {
      setError('Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  if (user?.is_pro) {
    return (
      <div
        className="max-w-2xl mx-auto bg-white rounded-2xl p-8 text-center"
        style={{ border: '1px solid var(--lf-line)' }}
      >
        <Crown size={32} style={{ color: '#f59e0b', margin: '0 auto 12px' }} />
        <h2 className="text-2xl font-bold mb-2">You’re on Pro</h2>
        <p className="text-sm mb-5" style={{ color: 'var(--lf-muted)' }}>
          All premium features are unlocked for your account.
        </p>
        <Button variant="secondary" onClick={onBack}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-3"
          style={{ background: '#eef2ff', color: '#3730a3' }}
        >
          <Sparkles size={13} /> Unlock the full toolkit
        </div>
        <h1 className="text-3xl font-bold mb-2">Lindore FreightAI Pro</h1>
        <p className="text-sm" style={{ color: 'var(--lf-muted)' }}>
          Everything you need to plan, negotiate, and dispatch loads faster.
        </p>
      </div>

      <div
        className="rounded-2xl bg-white p-6 md:p-8"
        style={{ border: '1px solid var(--lf-line)', boxShadow: '0 2px 7px rgba(15,23,42,.04)' }}
      >
        <div className="flex items-baseline justify-between flex-wrap mb-6">
          <div>
            <div className="text-sm font-semibold" style={{ color: 'var(--lf-blue)' }}>
              PRO
            </div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-5xl font-extrabold">${plan ? plan.price_display.replace('$', '') : '49'}</span>
              <span className="text-sm font-medium" style={{ color: 'var(--lf-muted)' }}>
                / month
              </span>
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--lf-muted)' }}>
              Cancel anytime. Taxes may apply.
            </div>
          </div>
          <Button variant="primary" onClick={handleCheckout} disabled={busy || !plan}>
            {busy ? (
              <>
                <Loader2 className="animate-spin" size={15} /> Redirecting…
              </>
            ) : (
              <>
                <Zap size={15} /> Upgrade to Pro
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(plan?.features || []).map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2 size={16} style={{ color: 'var(--lf-green)', marginTop: 2 }} />
              <span className="text-sm">{f}</span>
            </div>
          ))}
        </div>

        {error && <p className="text-xs text-red-600 mt-3">{error}</p>}

        <div
          className="mt-6 pt-5 flex items-center justify-between text-xs"
          style={{ borderTop: '1px solid var(--lf-line)', color: 'var(--lf-muted)' }}
        >
          <span>Secured by Stripe • All major cards accepted</span>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--lf-blue)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
